import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowLeft, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { getBaseURL } from '../api/client';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        if (password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (!token) {
            setError('Token de recuperación no encontrado');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.post(`${getBaseURL()}/auth/reset-password`, { token, password });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al restablecer la contraseña');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#0a0f16] flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-[#111827] border border-red-500/20 rounded-2xl p-8 text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Enlace inválido</h2>
                    <p className="text-gray-400 mb-8">El enlace de recuperación parece corrupto o ha caducado.</p>
                    <Link to="/forgot-password" className="text-[#00bfa5] hover:underline">Solicitar uno nuevo</Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-[#0a0f16] flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-[#111827] border border-gray-800 rounded-2xl p-8 text-center">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-[#00bfa5]/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-10 h-10 text-[#00bfa5]" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-4">Contraseña actualizada</h2>
                    <p className="text-gray-400 mb-8">Tu contraseña ha sido cambiada con éxito. Serás redirigido al inicio de sesión...</p>
                    <Link to="/login" className="text-[#00bfa5] hover:underline">Ir al login ahora</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0f16] flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-[#111827] border border-gray-800 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-2">Nueva contraseña</h2>
                <p className="text-gray-400 mb-8">Crea una contraseña fuerte para proteger tu cuenta.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nueva contraseña</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#1f2937] border border-gray-700 rounded-xl py-3 pl-10 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[#00bfa5] transition-all"
                                placeholder="Mínimo 6 caracteres"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Confirmar contraseña</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full bg-[#1f2937] border border-gray-700 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-[#00bfa5] transition-all"
                                placeholder="Repite la contraseña"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-xl text-sm">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#00bfa5] hover:bg-[#00d1b2] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Cambiar contraseña'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
